import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdVetaCenterProjectLinks } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { VetaCenterProjectLink } from '~hooks/useVetaCenter/models';
import connection from '~services/connectionModule';
import { fetchAllApiPages } from '~utils/fetchAllPages';

const VETA_CENTER_PROJECT_LINKS_QUERY_KEY = 'vetaCenterProjectLinks';

export const getVetaCenterProjectLinksQueryKey = (companyId: string) => [
  VETA_CENTER_PROJECT_LINKS_QUERY_KEY,
  companyId,
];

interface UseVetaCenterProjectLinksQueryProps {
  companyId: string;
  isEnabled: boolean;
}

export default function useVetaCenterProjectLinksQuery({
  companyId,
  isEnabled,
}: UseVetaCenterProjectLinksQueryProps) {
  const { data, isError, isLoading } = useQuery({
    queryKey: getVetaCenterProjectLinksQueryKey(companyId),
    queryFn: isEnabled
      ? async ({ signal }) => {
          try {
            return await fetchAllApiPages(
              (query, pageSignal) =>
                getV1CompaniesCompanyIdVetaCenterProjectLinks({
                  path: { 'company-id': companyId },
                  query,
                  signal: pageSignal,
                }),
              VetaCenterProjectLink.parse,
              { signal },
            );
          } catch (error) {
            if (signal.aborted) {
              throw error;
            }

            connection.handleRequestError(
              error,
              t('error_messages.veta_center.failed_to_fetch_project_links'),
            );
          }
        }
      : skipToken,
    retry: false,
  });

  return { isError, isLoading, projectLinks: data ?? [] };
}
